/**
 * Fund Line Component
 *
 * Renders the fund value line with area fill on the chart.
 */

import { CHART_CONFIG, CHART_COLORS } from "../constants";
import {
  getChartDimensions,
  indexToX,
  valueToY,
  generateSmoothPath,
  generateAreaPath,
} from "../utils";
import type { PerformanceDataPoint } from "../types";

interface FundLineProps {
  data: PerformanceDataPoint[];
  min: number;
  max: number;
}

/**
 * Fund value line with area fill
 */
export function FundLine({ data, min, max }: FundLineProps) {
  if (data.length === 0) return null;

  const { innerHeight } = getChartDimensions();
  const baseY = CHART_CONFIG.paddingTop + innerHeight;

  const points = data.map((point, index) => ({
    x: indexToX(index, data.length),
    y: valueToY(point.fundValue, min, max),
  }));

  const linePath = generateSmoothPath(points);
  const areaPath = generateAreaPath(points, baseY);

  return (
    <g className="fund-line">
      {/* Area fill under the line */}
      <path
        d={areaPath}
        fill={CHART_COLORS.fund.fill}
        className="transition-opacity duration-200"
      />
      {/* Main line */}
      <path
        d={linePath}
        fill="none"
        stroke={CHART_COLORS.fund.stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End point dot */}
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1]?.x}
          cy={points[points.length - 1]?.y}
          r={4}
          fill={CHART_COLORS.fund.stroke}
          stroke="white"
          strokeWidth={2}
        />
      )}
    </g>
  );
}
